import { X } from 'lucide-react';
import { Dialog as D } from 'radix-ui';
import { useEffect, useRef, type ReactNode } from 'react';
import { sound } from '@/lib/sound';
import { cn } from '@/lib/utils';
import { Button } from './button';

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onOpenChange(v: boolean): void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  useOpenSound(open);
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fx-overlay fixed inset-0 z-50 bg-slate-950/40 backdrop-blur-[6px] backdrop-saturate-150" />
        <D.Content
          className={cn(
            'fx-dialog fixed top-1/2 left-1/2 z-50 w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl border border-border bg-surface shadow-(--shadow-pop) ring-1 ring-white/40 focus:outline-none dark:ring-white/5',
            { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-3xl' }[size],
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-border bg-linear-to-b from-surface-2/70 to-surface px-5 py-3.5">
            <div>
              <D.Title className="text-[15px] font-semibold">{title}</D.Title>
              {description ? (
                <D.Description className="mt-0.5 text-[13px] text-fg-muted">{description}</D.Description>
              ) : (
                <D.Description className="sr-only">{typeof title === 'string' ? title : 'Dialog'}</D.Description>
              )}
            </div>
            <D.Close asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Close">
                <X />
              </Button>
            </D.Close>
          </div>
          {children && <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>}
          {footer && <div className="flex justify-end gap-2 border-t border-border bg-surface-2 px-5 py-3 rounded-b-lg">{footer}</div>}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  tone = 'primary',
  loading,
  onConfirm,
  children,
}: {
  open: boolean;
  onOpenChange(v: boolean): void;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  tone?: 'primary' | 'danger';
  loading?: boolean;
  onConfirm(): void;
  children?: ReactNode;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !loading && onOpenChange(v)}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
    </Dialog>
  );
}

/** The open / close sounds of dialogs and side panels (only on a change, never on the first render closed). */
export function useOpenSound(open: boolean) {
  const was = useRef(false);
  useEffect(() => {
    if (open && !was.current) sound.open();
    else if (!open && was.current) sound.close();
    was.current = open;
  }, [open]);
}
